import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? '';

export const TOKEN_STORAGE_KEY = 'em.auth.token';
export const CART_SESSION_STORAGE_KEY = 'em.cart.session';

export const api: AxiosInstance = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  const token = typeof window !== 'undefined' ? window.localStorage.getItem(TOKEN_STORAGE_KEY) : null;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  // Attach anonymous cart session header for cart calls when there is no JWT.
  const url = config.url ?? '';
  if (!token && url.includes('/api/cart')) {
    const session = typeof window !== 'undefined' ? window.localStorage.getItem(CART_SESSION_STORAGE_KEY) : null;
    if (session) {
      config.headers.set('X-Cart-Session', session);
    }
  }
  return config;
});

// 401 handler: clear token, push to /login preserving next. Silence for /auth/me
// hydration and /auth/logout where 401 is expected.
api.interceptors.response.use(
  (resp) => resp,
  (error: AxiosError) => {
    if (typeof window !== 'undefined' && error.response?.status === 401) {
      const url = error.config?.url ?? '';
      const ignore = url.includes('/api/auth/me')
        || url.includes('/api/auth/logout')
        || url.includes('/api/auth/login')
        || url.includes('/api/auth/register')
        || url.includes('/api/auth/verify-email')
        || url.includes('/api/auth/2fa/login')
        || url.includes('/api/auth/forgot-password')
        || url.includes('/api/auth/reset-password')
        || url.includes('/api/admin/');
      if (!ignore) {
        window.localStorage.removeItem(TOKEN_STORAGE_KEY);
        const onLogin = window.location.pathname.startsWith('/login');
        if (!onLogin) {
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.assign(`/login?next=${next}`);
        }
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Translate an unknown error (usually an axios error) into a human-readable
 * message using the backend's {code, message} shape when present.
 */
export function getErrorMessage(err: unknown, fallback = 'Something went wrong.'): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { code?: string; message?: string } | undefined;
    if (data && typeof data.message === 'string' && data.message.length > 0) {
      return data.message;
    }
    const status = err.response?.status;
    if (status && status >= 500) return 'Server error. Please try again.';
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function getErrorCode(err: unknown): string | null {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { code?: string } | undefined;
    if (data && typeof data.code === 'string') return data.code;
  }
  return null;
}

/**
 * When no anonymous cart session exists, create one and persist it so all
 * subsequent cart calls are scoped to the same server-side cart.
 */
export function ensureCartSessionKey(): string {
  if (typeof window === 'undefined') return '';
  let key = window.localStorage.getItem(CART_SESSION_STORAGE_KEY);
  if (!key) {
    key = (crypto && typeof crypto.randomUUID === 'function')
      ? crypto.randomUUID()
      : `cart-${Math.random().toString(36).slice(2)}-${Date.now()}`;
    window.localStorage.setItem(CART_SESSION_STORAGE_KEY, key);
  }
  return key;
}

export function clearCartSessionKey(): void {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(CART_SESSION_STORAGE_KEY);
}
